import { Component, Inject, OnDestroy } from '@angular/core';
import { DocumentNode, AuthorNode, User } from '@completion/models';
import { Store } from '@ngrx/store';
import { Subject, Observable, of } from 'rxjs';
import { takeUntil, take } from 'rxjs/operators';
import { selectEditMode, getCurrentUser, getCurrentDocument, getProjectEditorsAndAdmins } from '@completion/selectors';
import { FormControl } from '@angular/forms';
import { DocumentUpdateQueueService } from '../../../../core/services/document-update-queue.service';
import { MatDialog } from '@angular/material/dialog';
import { UserSelectDialogComponent, UserSelectDialogData } from '../../../../shared/user-select/user-select-dialog.component';
import { RemoveUserDialogComponent } from '../../../../shared/remove-user-dialog/remove-user-dialog.component';
import { persistNodeUpdateForNode } from '../node-persistence.util';
import { collectAssignedUserIds, filterUnassignedCandidates } from '../../../../shared/utils/document-user-exclusion.util';

@Component({
  selector: 'app-author',
  templateUrl: './author.component.html',
  styleUrls: ['./author.component.scss'],
  standalone: false,
})
export class AuthorComponent implements OnDestroy {
  documentNode: DocumentNode;
  editMode = false;
  currentUser: User = null;

  control = new FormControl('');
  private readonly destroy$ = new Subject();
  users$: Observable<User[]> = of([]);

  constructor(
    @Inject('documentNode') documentNode: DocumentNode,
    private store: Store,
    private updateQueue: DocumentUpdateQueueService,
    private dialog: MatDialog,
  ) {
    this.documentNode = documentNode;
    this.store
      .select(selectEditMode)
      .pipe(takeUntil(this.destroy$))
      .subscribe((m) => (this.editMode = m));
    this.store
      .select(getCurrentUser)
      .pipe(takeUntil(this.destroy$))
      .subscribe((u) => (this.currentUser = u));

    try {
      this.users$ = this.store.select(getProjectEditorsAndAdmins);
    } catch {
      this.users$ = of([]);
    }
  }

  get author(): AuthorNode | null {
    return (this.documentNode as AuthorNode) || null;
  }

  get isAssignedCurrentUser(): boolean {
    const node = this.documentNode as AuthorNode;
    const assignedId = node && node.user ? node.user.id || null : null;
    const currentId = this.currentUser ? this.currentUser.id || null : null;
    return assignedId != null && currentId != null && String(assignedId) === String(currentId);
  }

  selectUser(user: User) {
    if (this.isAssignedCurrentUser) {
      this.control.setValue('');
      return;
    }

    const node = this.documentNode as AuthorNode;
    const newContent = user?.name || '';

    // Clear the input; don't mutate the injected node directly (it may be frozen/non-extensible)
    this.control.setValue('');

    // Use the shared helper to clone the document, update the node and enqueue the change
    persistNodeUpdateForNode(this.store, this.updateQueue, node, (n: any) => {
      n.user = user;
      n.content = newContent;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  removeAuthor() {
    // Present options: unassign user or delete the author node
    const ref = this.dialog.open(RemoveUserDialogComponent, {
      width: '500px',
      data: { title: 'Remove author', message: 'Choose whether to unassign the user from this author node or delete the entire node.' },
      panelClass: 'remove-user-panel',
    });
    ref
      .afterClosed()
      .pipe(take(1))
      .subscribe((res: any) => {
        if (!res) return;
        if (res === 'unassign') {
          // Clear user and content on the author node
          const node = this.documentNode as AuthorNode;
          persistNodeUpdateForNode(this.store, this.updateQueue, node, (n: any) => {
            n.user = null;
            n.content = '';
          });
          return;
        }
        if (res === 'delete') {
          // Remove the entire node immutably from the current document
          this.store
            .select(getCurrentDocument)
            .pipe(take(1))
            .subscribe((doc) => {
              if (!doc) return;
              const cloned = JSON.parse(JSON.stringify(doc));
              const removeNodeByIdImmutable = (nodes: any[], id: number): { nodes: any[]; removed: boolean } => {
                if (!Array.isArray(nodes) || nodes.length === 0) return { nodes, removed: false };
                let removed = false;
                const next: any[] = [];
                for (const n of nodes) {
                  if (!n) continue;
                  if (n.id === id) {
                    removed = true;
                    continue;
                  }
                  let updatedChildren = n.children;
                  if (Array.isArray(n.children) && n.children.length) {
                    const res = removeNodeByIdImmutable(n.children, id);
                    if (res.removed) {
                      updatedChildren = res.nodes;
                      removed = true;
                    }
                  }
                  next.push(updatedChildren !== n.children ? { ...n, children: updatedChildren } : n);
                }
                return removed ? { nodes: next, removed: true } : { nodes, removed: false };
              };
              const { nodes: updatedNodes, removed } = removeNodeByIdImmutable(cloned.nodes || [], (this.documentNode as any).id);
              if (!removed) return;
              cloned.nodes = updatedNodes;
              // Assign ordinals and queue immediate update to persist deletion
              try {
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                (this.updateQueue as any).assignOrdinals ? (this.updateQueue as any).assignOrdinals(cloned) : null;
              } catch {}
              // Prefer immediate persistence for structural change
              if ((this.updateQueue as any).queueImmediate) {
                (this.updateQueue as any).queueImmediate(cloned);
              } else {
                this.updateQueue.queue(cloned);
              }
            });
        }
      });
  }

  editAuthor() {
    if (this.isAssignedCurrentUser) {
      return;
    }
    const preselectedId = this.author?.user?.id ?? undefined;
    // Compute effective document (prefer any pending queued update)
    this.store
      .select(getCurrentDocument)
      .pipe(take(1))
      .subscribe((doc) => {
        const pending = (this.updateQueue as any).getLastQueued ? (this.updateQueue as any).getLastQueued() : null;
        const effectiveDoc = pending || doc;
        // Collect assigned ids and exclude them except the preselected one
        const assignedIds = collectAssignedUserIds(effectiveDoc);
        const excludeSet = new Set<number>(assignedIds.filter((id) => id !== (preselectedId == null ? undefined : Number(preselectedId))));
        // Also exclude current user if present
        const myId = this.currentUser && (this.currentUser as any).id != null ? Number((this.currentUser as any).id) : undefined;
        if (myId != null && myId !== (preselectedId == null ? undefined : Number(preselectedId))) {
          excludeSet.add(myId);
        }
        const excludeIds = Array.from(excludeSet);

        // Prepare filtered candidate list and open dialog
        this.users$.pipe(take(1)).subscribe((list) => {
          const filtered = filterUnassignedCandidates(list || [], effectiveDoc);
          const final = filtered.filter((uitem) => {
            const uid = (uitem as any).id;
            if (preselectedId != null && String(uid) === String(preselectedId)) return true;
            if (myId != null && String(uid) === String(myId)) return false;
            return true;
          });

          const data: UserSelectDialogData = {
            title: 'Select author',
            users: final,
            preselectedUserId: preselectedId,
            excludeUserIds: excludeIds,
          };
          const ref = this.dialog.open(UserSelectDialogComponent, { width: '420px', data });
          ref
            .afterClosed()
            .pipe(take(1))
            .subscribe((user: User | undefined) => {
              if (user) {
                this.selectUser(user);
              }
            });
        });
      });
  }
}
